import * as React from "react"
import styled from "styled-components"
import * as EmailValidator from "email-validator"
import * as ReactGA from "react-ga"
import { Ring } from "react-awesome-spinners"

import Box from "../../lib/ui/ui-box"
import Input from "../../components/input"
import Button from "../../components/button"
import Text from "../../components/text"
import Colors from "../../theme/colors"

const LANDING_IMAGE_SRC = "/images/landing_image.jpg"

const Wrapper = styled(Box)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 64px);
  margin: 0 140px;
  @media (max-width: 1300px) {
    margin: 0 64px;
  }
  @media (max-width: 900px) {
    margin: 24px 64px;
    height: initial;
  }
  @media (max-width: 600px) {
    margin: 24px;
  }
`

const MainSection = styled(Box)`
  justify-content: space-between;
  align-items: center;
  height: 550px;
  @media (max-width: 900px) {
    flex-direction: column-reverse;
    height: 100%;
    justify-content: flex-end;
  }
`

const Form = styled.form`
  width: 100%;
`

const InputButtonWrapper = styled(Box)`
  width: 100%;
  @media (max-width: 650px) {
    flex-direction: column;
  }
  input {
    width: 100%;
    min-width: initial;
    box-shadow: 0 4px 16px 0px rgb(0 0 0 / 5%);
    @media (max-width: 650px) {
      min-width: 100%;
      margin-bottom: 10px;
      height: 40px;
      box-shadow: none;
    }
  }
`

const StyledInput = styled(Input)`
  width: 100%;
`

const ErrorText = styled(Text)<any>`
  opacity: 0;
  ${(props) => props.error && `opacity: 1`}
  @media (max-width: 480px) {
    font-size: 16px;
  }
`

const SuccessText = styled(Text)`
  @media (max-width: 480px) {
    font-size: 22px;
  }
  @media (max-width: 360px) {
    font-size: 20px;
  }
`

const LeftSide = styled(Box)`
  flex-direction: column;
  justify-content: center;
  max-width: 629px;
  padding-right: 72px;
  flex-basis: 50%;
  @media(max-width: 1330px) {
    padding-right: 55px;
  }
  @media(max-width: 1100px) {
    padding-right: 40px;
  }
  @media(max-width: 900px) {
    width: 100%
    display: initial;
    flex-basis: initial;
    padding-right: initial;
  }
`

const TextWrapper = styled(Box)`
  @media (max-width: 900px) {
    margin-bottom: 32px;
  }
  @media (max-width: 480px) {
    margin-bottom: 32px;
  }
  @media (max-width: 360px) {
    margin-bottom: 24px;
  }
`

const Title = styled(Text)`
  font-family: Avenir Bold;
  @media (max-width: 1300px) {
    font-size: 42px;
  }
  @media (max-width: 1120px) {
    font-size: 32px;
  }
  @media (max-width: 360px) {
    font-size: 28px;
    margin-bottom: 16px;
  }
`

const SubTitleWrapper = styled(Box)`
  max-width: 460px;
  width: 100%;
  @media (max-width: 1300px) {
    ${Text} {
      font-size: 24px;
    }
  }
  @media (max-width: 480px) {
    ${Text} {
      font-size: 22px;
    }
    margin-bottom: initial;
  @media (max-width: 360px) {
      ${Text} {
        font-size: 20px;
      }
  }
`

const ImageWrapper = styled.div`
  display: flex;
  width: 550px;
  height: auto;

  @media (max-width: 1300px) {
    max-width: 500px;
    max-height: 500px;
  }
  @media (max-width: 1120px) {
    max-width: 400px;
    max-height: 400px;
  }
  @media (max-width: 900px) {
    width: 100%;
    margin-bottom: 24px;
  }
  @media (max-width: 450px) {
    flex-basis: initial;
  }
`

const LandingImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 16px 0px rgb(0 0 0 / 5%);
`

const StyledButton = styled(Button)`
  position: absolute;
  right: 10px;
  top: 2px;
  width: 44px;
  max-width: 143px;
  width: 100%;
  min-width: initial;
  @media (min-width: 900px) and (max-width: 1100px) {
    max-width: 115px;
  }
  @media (max-width: 650px) {
    position: initial;
    display: block;
    max-width: 100%;
    width: 100%;
  }
  @media (max-width: 650px) {
    min-width: 100%;
    height: 40px;
  }
`

class LandingContent extends React.Component {
  state = {
    email: "",
    error: false,
    loading: false,
    success: false,
  }

  handleEmailChange = (e) => {
    this.setState({ email: e.target.value, error: false })
  }

  handleSubmit = async (e) => {
    e.preventDefault()

    const { email } = this.state

    const isEmailValid = EmailValidator.validate(email)

    if (!isEmailValid) {
      return this.setState({ error: true })
    }

    this.setState({ loading: true })

    const lowerCaseEmail = email.toLowerCase()

    const now = new Date().toISOString()
    try {
      const fields = {
        email: lowerCaseEmail,
        createdTime: now,
      }
      await fetch("/.netlify/functions/airtable", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(fields),
      })
      this.setState({ loading: false, success: true })
      ReactGA.event({
        category: "Sign Up",
        action: "User signed up for waitlist",
      })
    } catch (error) {
      this.setState({ loading: false })
      console.log(error)
    }
  }

  render() {
    const { email, error, loading, success } = this.state
    return (
      <Wrapper>
        <MainSection>
          <LeftSide>
            <TextWrapper flexDirection="column">
              <Title
                color={Colors.primaryBlack}
                fontSize={45}
                textAlign="left"
                mb={16}
              >
                Discover dog friendly places and hikes near you.
              </Title>
              <SubTitleWrapper mb={24}>
                <Text
                  color={Colors.secondaryGrey}
                  fontSize={28}
                  fontWeight={500}
                  textAlign="left"
                >
                  We make it easy for you to spend more time with your dog.
                </Text>
              </SubTitleWrapper>
            </TextWrapper>
            <Box w="100%" justifyContent="center">
              {success && (
                <Box>
                  <SuccessText
                    color={Colors.primaryBlack}
                    fontSize={24}
                    fontWeight={500}
                    textAlign="left"
                    mb={24}
                  >
                    👋🏼 Thanks for joining! <br />
                    We are currently in private beta but are opening things up
                    soon. We look forward to welcoming you!
                  </SuccessText>
                </Box>
              )}
              {loading && (
                <Box justifyContent="center">
                  <Ring color={Colors.primaryBlack} />
                </Box>
              )}
              {!success && !loading && (
                <Form onSubmit={this.handleSubmit}>
                  <InputButtonWrapper>
                    <StyledInput
                      placeholder="Enter your email"
                      value={email}
                      onChange={this.handleEmailChange}
                    />
                    <StyledButton type="submit">Join Waitlist</StyledButton>
                  </InputButtonWrapper>
                  <ErrorText my={2} color={Colors.secondaryGrey} error={error}>
                    Please provide a valid email
                  </ErrorText>
                </Form>
              )}
            </Box>
          </LeftSide>
          <ImageWrapper>
            <LandingImage src={LANDING_IMAGE_SRC} />
          </ImageWrapper>
        </MainSection>
      </Wrapper>
    )
  }
}

export default LandingContent



// WEBPACK FOOTER //
// ./src/components/landing/landing-content.component.tsx